// mcn-frontend/src/App.js
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { UserProvider, useUser } from "./contexts/UserContext";
import AntdProvider from './theme/AntdProvider';
import Layout from './components/layout/Layout';
import getTenantConfig from './config/tenant-config';
import { validateDomain } from './utils/domainValidator';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingFallback from './components/common/LoadingFallback.jsx';
import InvestmentOpportunities from './components/InvestmentOpportunities';

// Styles
import './App.css';

// Core components (immediate load)
import Homepage from './components/Homepage';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Unauthorized from './pages/Unauthorized';
import CreatorApplication from './components/CreatorApplication';
import ProtectedRoute, {
  AdminRoute,
  ManagerRoute,
  ContentModeratorRoute,
  FinanceRoute,
  AggregatorRoute,
  ChannelOwnerRoute,
  CreatorRoute,
  InvestorRoute,
  StudioRoute,
  SupportRoute,
  AnalyticsRoute,
  SuperAdminOnlyRoute,
  DashboardRoute
} from './components/auth/ProtectedRoute';

// Lazy-loaded dashboard components (load on demand)
const AdminDashboard = lazy(() => import('./components/dashboards/AdminDashboard'));
const SuperAdminDashboard = lazy(() => import('./components/dashboards/SuperAdminDashboard'));
const TenantAdminDashboard = lazy(() => import('./components/dashboards/TenantAdminDashboard'));
const ManagerDashboard = lazy(() => import('./components/dashboards/ManagerDashboard'));
const FinanceDashboard = lazy(() => import('./components/dashboards/FinanceDashboard'));
const ContentModerationDashboard = lazy(() => import('./components/dashboards/ContentModerationDashboard'));
const AggregatorDashboard = lazy(() => import('./components/dashboards/AggregatorDashboard'));
const ChannelOwnerDashboard = lazy(() => import('./components/dashboards/ChannelOwnerDashboard'));
const CreatorDashboard = lazy(() => import('./components/dashboards/CreatorDashboard'));
const StudioDashboard = lazy(() => import('./components/dashboards/StudioDashboard'));
const InvestorDashboard = lazy(() => import('./components/dashboards/InvestorDashboard'));
const SupportDashboard = lazy(() => import('./components/dashboards/SupportDashboard'));
const AnalyticsDashboard = lazy(() => import('./components/dashboards/AnalyticsDashboard'));
const RevenueShareDashboard = lazy(() => import('./components/dashboards/RevenueShareDashboard'));

// Lazy-loaded feature components
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const AdminProjects = lazy(() => import('./components/admin/AdminProjects'));
const AdminProjectEdit = lazy(() => import('./components/admin/AdminProjectEdit'));
const AdminApplications = lazy(() => import('./components/AdminApplications'));
const VideoUpload = lazy(() => import('./components/VideoUpload'));

// Reports module
const ReportsCatalog = lazy(() => import('./reports/ReportsCatalog'));
const RevenueTrendsReport = lazy(() => import('./reports/pages/financial/RevenueTrendsReport'));
const PayoutsReport = lazy(() => import('./reports/pages/financial/PayoutsReport'));
const InvestmentFlowReport = lazy(() => import('./reports/pages/financial/InvestmentFlowReport'));
const PnLReport = lazy(() => import('./reports/pages/financial/PnLReport'));
const VideoPerformanceReport = lazy(() => import('./reports/pages/content/VideoPerformanceReport'));
const ModerationPipelineReport = lazy(() => import('./reports/pages/content/ModerationPipelineReport'));
const CrossPlatformReport = lazy(() => import('./reports/pages/content/CrossPlatformReport'));
const ChannelGrowthReport = lazy(() => import('./reports/pages/channels/ChannelGrowthReport'));
const CreatorEarningsReport = lazy(() => import('./reports/pages/people/CreatorEarningsReport'));
const InvestorPortfolioReport = lazy(() => import('./reports/pages/people/InvestorPortfolioReport'));
const AggregatorCommissionsReport = lazy(() => import('./reports/pages/people/AggregatorCommissionsReport'));
const UserActivityReport = lazy(() => import('./reports/pages/people/UserActivityReport'));
const AuditLogReport = lazy(() => import('./reports/pages/operations/AuditLogReport'));
const ApplicationFunnelReport = lazy(() => import('./reports/pages/operations/ApplicationFunnelReport'));
const SystemHealthReport = lazy(() => import('./reports/pages/operations/SystemHealthReport'));
const TaxReport = lazy(() => import('./reports/pages/operations/TaxReport'));
const PlaceholderReport = lazy(() => import('./reports/pages/PlaceholderReport'));

// Account / Auth pages
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const AccountSettings = lazy(() => import('./pages/auth/AccountSettings'));
const Onboarding = lazy(() => import('./pages/auth/Onboarding'));
const BulkInvite = lazy(() => import('./pages/admin/BulkInvite'));
const LoginChallenge = lazy(() => import('./pages/auth/LoginChallenge'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const About = lazy(() => import('./pages/public/About'));
const FAQ = lazy(() => import('./pages/public/FAQ'));
const Contact = lazy(() => import('./pages/public/Contact'));
const Channels = lazy(() => import('./pages/public/Channels'));
const MoviesMarketplace = lazy(() => import('./pages/public/MoviesMarketplace'));
const MovieDetailPublic = lazy(() => import('./pages/public/MovieDetailPublic'));
const ListYourProject = lazy(() => import('./pages/public/ListYourProject'));
const MyListings = lazy(() => import('./pages/producer/MyListings'));
const ListingPayments = lazy(() => import('./pages/admin/ListingPayments'));
const ListingReview = lazy(() => import('./pages/admin/ListingReview'));
const AdminInquiries = lazy(() => import('./pages/admin/Inquiries'));
const ProducerAgreement = lazy(() => import('./pages/public/ProducerAgreement'));
const CommunityGuidelines = lazy(() => import('./pages/public/CommunityGuidelines'));
const CopyrightPolicy = lazy(() => import('./pages/public/CopyrightPolicy'));
const Partners = lazy(() => import('./pages/public/Partners'));
const InvestInfo = lazy(() => import('./pages/public/InvestInfo'));
const Help = lazy(() => import('./pages/Help'));
const Wallet = lazy(() => import('./pages/Wallet'));
const MyChannels = lazy(() => import('./pages/MyChannels'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const ChannelCreators = lazy(() => import('./pages/channel/ChannelCreators'));
const InvestMovies = lazy(() => import('./pages/invest/Movies'));
const InvestCreators = lazy(() => import('./pages/invest/Creators'));
const InvestPortfolio = lazy(() => import('./pages/invest/Portfolio'));
const InvestAcquisitions = lazy(() => import('./pages/invest/Acquisitions'));
const AcquisitionPipeline = lazy(() => import('./pages/admin/AcquisitionPipeline'));
const ContentEditor = lazy(() => import('./pages/admin/ContentEditor'));



// Route path constants
const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  UNAUTHORIZED: '/unauthorized',
  APPLY: '/apply',
  DASHBOARD: '/dashboard',
  
  // Admin routes
  ADMIN_DASHBOARD: '/admin-dashboard',
  ADMIN_APPLICATIONS: '/admin/applications',
  ADMIN_PROJECTS: '/admin/projects',
  ADMIN_PROJECT_EDIT: '/admin/projects/edit/:projectId',
  
  // Dashboard routes by role
  SUPER_ADMIN_DASHBOARD: '/super-admin-dashboard',
  TENANT_ADMIN_DASHBOARD: '/tenant-admin-dashboard',
  MANAGER_DASHBOARD: '/manager-dashboard',
  FINANCE_DASHBOARD: '/finance-dashboard',
  CONTENT_MODERATION: '/content-moderation',
  AGGREGATOR_DASHBOARD: '/aggregator-dashboard',
  CHANNEL_DASHBOARD: '/channel-dashboard',
  CREATOR_DASHBOARD: '/creator-dashboard',
  STUDIO_DASHBOARD: '/studio-dashboard',
  INVESTOR_DASHBOARD: '/investor-dashboard',
  SUPPORT_DASHBOARD: '/support-dashboard',
  ANALYTICS_DASHBOARD: '/analytics-dashboard',
  
  // Feature routes
  PROJECT_DETAIL: '/project/:projectId',
  REVENUE_SHARE: '/revenue-share',
  UPLOAD: '/upload',
  
  // Content routes
  CONTENT_PENDING: '/content/pending',
  CONTENT_APPROVED: '/content/approved',
  CONTENT_REJECTED: '/content/rejected',
  
  // Investment routes
  INVESTMENTS: '/investments',
  INVESTMENT_MARKETPLACE: '/investment-marketplace',
  INVESTMENT_OPPORTUNITIES: '/investment-opportunities', 
  
  // Aggregator routes
  AGGREGATOR_REFERRALS: '/aggregator/referrals',
  AGGREGATOR_COMMISSIONS: '/aggregator/commissions',
  AGGREGATOR_APPLICATIONS: '/aggregator/applications',
  
  // Finance routes
  FINANCE_PAYMENTS: '/finance/payments',
  FINANCE_REVENUE: '/finance/revenue',
  
  // Analytics routes
  ANALYTICS_OVERVIEW: '/analytics/overview',
  ANALYTICS_CHANNELS: '/analytics/channels',
};

// Role to dashboard mapping
const ROLE_DASHBOARD_MAP = {
  'SUPER_ADMIN': ROUTES.SUPER_ADMIN_DASHBOARD,
  'TENANT_ADMIN': ROUTES.TENANT_ADMIN_DASHBOARD,
  'ADMIN': ROUTES.ADMIN_DASHBOARD,
  'MANAGER': ROUTES.MANAGER_DASHBOARD,
  'MCN_MANAGER': ROUTES.MANAGER_DASHBOARD,
  'FINANCE_MANAGER': ROUTES.FINANCE_DASHBOARD,
  'CONTENT_MANAGER': ROUTES.CONTENT_MODERATION,
  'AGGREGATOR': ROUTES.AGGREGATOR_DASHBOARD,
  'CHANNEL_OWNER': ROUTES.CHANNEL_DASHBOARD,
  'CREATOR': ROUTES.CREATOR_DASHBOARD,
  'CONTENT_CREATOR': ROUTES.CREATOR_DASHBOARD,
  'STUDIO': ROUTES.STUDIO_DASHBOARD,
  'STUDIO_ADMIN': ROUTES.STUDIO_DASHBOARD,
  'STUDIO_MEMBER': ROUTES.STUDIO_DASHBOARD,
  'INVESTOR': ROUTES.INVESTOR_DASHBOARD,
  'PREMIUM_INVESTOR': ROUTES.INVESTOR_DASHBOARD,
  'SUPPORT_AGENT': ROUTES.SUPPORT_DASHBOARD,
  'SUPPORT': ROUTES.SUPPORT_DASHBOARD,
  'ANALYTICS_VIEWER': ROUTES.ANALYTICS_DASHBOARD,
  'VIEWER': ROUTES.DASHBOARD,
  'MODERATOR': ROUTES.DASHBOARD,
  'USER': ROUTES.CREATOR_DASHBOARD
};

// Generic Dashboard Router - Redirects to appropriate dashboard based on role
const DashboardRouter = () => {
  const { currentUser, loading } = useUser();
  
  if (loading) {
    return <LoadingFallback message="Loading your dashboard..." />;
  }
  
  if (!currentUser) return <Navigate to={ROUTES.LOGIN} />;
  
  const targetRoute = ROLE_DASHBOARD_MAP[currentUser.role] || ROUTES.DASHBOARD;
  return <Navigate to={targetRoute} replace />;
};

// 404 Not Found Component
const NotFound = () => (
  <div className="not-found">
    <h1>404 - Page Not Found</h1>
    <p>The page you're looking for doesn't exist.</p>
    <a href={ROUTES.DASHBOARD}>Go to Dashboard</a>
  </div>
);

function App() {
  const tenantConfig = getTenantConfig();
  
  // Set document title and colors based on tenant
  useEffect(() => {
    document.title = tenantConfig.name;
    // Update CSS variables for colors
    document.documentElement.style.setProperty('--primary-color', tenantConfig.primaryColor);
    document.documentElement.style.setProperty('--secondary-color', tenantConfig.secondaryColor);
    
    // Add more brand colors if needed
    document.documentElement.style.setProperty('--accent-color', tenantConfig.accentColor || '#FFC107');
    document.documentElement.style.setProperty('--text-color', tenantConfig.textColor || '#333333');
    document.documentElement.style.setProperty('--bg-color', tenantConfig.backgroundColor || '#FFFFFF');
  }, [tenantConfig]);

  // Validate domain access
  useEffect(() => {
    try {
      validateDomain();
    } catch (error) {
      console.error('Domain validation error:', error);
      // Could redirect to an unauthorized domain page here
    }
  }, []);

  return (
    <ErrorBoundary>
      <AntdProvider>
        <UserProvider>
          <Router>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* ===== PUBLIC ROUTES (wrapped in Layout for site shell — header + footer) ===== */}
              <Route path={ROUTES.LOGIN} element={<Layout><Login /></Layout>} />
              <Route path={ROUTES.REGISTER} element={<Layout><Register /></Layout>} />
              <Route path="/signup" element={<Navigate to={ROUTES.REGISTER} replace />} />
              <Route path={ROUTES.UNAUTHORIZED} element={<Layout><Unauthorized /></Layout>} />
              <Route path={ROUTES.APPLY} element={<Layout><CreatorApplication /></Layout>} />

              {/* ===== ACCOUNT / AUTH PAGES (wrapped in Layout) ===== */}
              <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
              <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
              <Route path="/verify-email" element={<Layout><VerifyEmail /></Layout>} />
              <Route path="/settings" element={
                <Layout>
                  <ProtectedRoute>
                    <AccountSettings />
                  </ProtectedRoute>
                </Layout>
              } />
              <Route path="/onboard" element={<Layout><Onboarding /></Layout>} />
              <Route path="/login-2fa" element={<Layout><LoginChallenge /></Layout>} />
              <Route path="/privacy" element={<Layout><PrivacyPolicy /></Layout>} />
              <Route path="/terms" element={<Layout><TermsOfService /></Layout>} />
              {/* Public marketing / info pages — linked from footer */}
              <Route path="/about" element={<Layout><About /></Layout>} />
              <Route path="/faq" element={<Layout><FAQ /></Layout>} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              <Route path="/channels" element={<Layout><Channels /></Layout>} />
              {/* Public projects catalog (Media Rights Co-Financing).
                  /projects is the canonical URL. /movies and /catalog kept
                  as aliases so old links still work. */}
              <Route path="/projects" element={<Layout><MoviesMarketplace /></Layout>} />
              {/* Producer-listed (ContentProject) detail page — uses same component;
                  detects vehicle type from URL prefix /cp/ to call the right API.
                  This route must come BEFORE the catch-all /projects/:id below. */}
              <Route path="/projects/cp/:id" element={<Layout><MovieDetailPublic /></Layout>} />
              <Route path="/projects/:id" element={<Layout><MovieDetailPublic /></Layout>} />
              <Route path="/movies" element={<Layout><MoviesMarketplace /></Layout>} />
              <Route path="/movies/:id" element={<Layout><MovieDetailPublic /></Layout>} />
              <Route path="/catalog" element={<Layout><MoviesMarketplace /></Layout>} />
              <Route path="/catalog/:id" element={<Layout><MovieDetailPublic /></Layout>} />
              {/* Phase B: Producer self-serve project listing. Public page —
                  pricing tiers + add-ons + submission form. Backend creates
                  PENDING_APPROVAL ContentProject. Aliases /submit, /producer for SEO. */}
              <Route path="/list-your-project" element={<Layout><ListYourProject /></Layout>} />
              <Route path="/submit" element={<Layout><ListYourProject /></Layout>} />
              <Route path="/for-producers" element={<Layout><ListYourProject /></Layout>} />
              {/* Producer dashboard - logged in producer manages their listings + sees inquiries */}
              <Route path="/my-listings" element={<Layout><ProtectedRoute><MyListings /></ProtectedRoute></Layout>} />
              {/* Public Producer Agreement legal page */}
              <Route path="/producer-agreement" element={<Layout><ProducerAgreement /></Layout>} />
              {/* Admin: Listing fee queue - mark paid or waive */}
              <Route path="/admin/listing-payments" element={<Layout><ProtectedRoute><ListingPayments /></ProtectedRoute></Layout>} />
              {/* Admin: Quality review queue */}
              <Route path="/admin/listing-review" element={<Layout><ProtectedRoute><ListingReview /></ProtectedRoute></Layout>} />
              {/* Admin: Inquiry inbox */}
              <Route path="/admin/inquiries" element={<Layout><ProtectedRoute><AdminInquiries /></ProtectedRoute></Layout>} />
              <Route path="/guidelines" element={<Layout><CommunityGuidelines /></Layout>} />
              <Route path="/community-guidelines" element={<Navigate to="/guidelines" replace />} />
              <Route path="/copyright" element={<Layout><CopyrightPolicy /></Layout>} />
              <Route path="/partners" element={<Layout><Partners /></Layout>} />
              <Route path="/invest-info" element={<Layout><InvestInfo /></Layout>} />
              {/* Footer "For Creators" / "For Investors" point at homepage anchors */}
              <Route path="/creators" element={<Navigate to="/#features" replace />} />
              <Route path="/investors" element={<Navigate to="/" replace />} />
              <Route path="/help" element={<Layout><ProtectedRoute><Help /></ProtectedRoute></Layout>} />
              <Route path="/wallet" element={<Layout><ProtectedRoute><Wallet /></ProtectedRoute></Layout>} />
              <Route path="/my-channels" element={<Layout><ProtectedRoute><MyChannels /></ProtectedRoute></Layout>} />
              <Route path="/portfolio" element={<Layout><ProtectedRoute><Portfolio /></ProtectedRoute></Layout>} />
              <Route path="/channel/:channelId/creators" element={<Layout><ProtectedRoute><ChannelCreators /></ProtectedRoute></Layout>} />
              {/* Investment routes disabled June 2026 - pending regulated entity (SCA / ADGM / AIF licence).
                  Code stays in tree; routes redirect to /. Admin pages remain for internal deal tracking only. */}
              <Route path="/invest/*" element={<Navigate to="/" replace />} />
              <Route path="/admin/acquisitions" element={<Layout><ProtectedRoute><AcquisitionPipeline /></ProtectedRoute></Layout>} />
              <Route path="/admin/content" element={<Layout><ProtectedRoute><ContentEditor /></ProtectedRoute></Layout>} />
              <Route path="/admin/invites" element={
                <Layout>
                  <ProtectedRoute>
                    <BulkInvite />
                  </ProtectedRoute>
                </Layout>
              } />

              {/* ===== DASHBOARD ROUTER ===== */}
              <Route 
                path={ROUTES.DASHBOARD} 
                element={
                  <ProtectedRoute>
                    <DashboardRouter />
                  </ProtectedRoute>
                } 
              />
              
              {/* ===== ADMIN ROUTES ===== */}
              <Route path={ROUTES.SUPER_ADMIN_DASHBOARD} element={
                <Layout>
                  <SuperAdminOnlyRoute>
                    <SuperAdminDashboard />
                  </SuperAdminOnlyRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.TENANT_ADMIN_DASHBOARD} element={
                <Layout>
                  <ProtectedRoute roles={['TENANT_ADMIN', 'SUPER_ADMIN']}>
                    <TenantAdminDashboard />
                  </ProtectedRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.ADMIN_DASHBOARD} element={
                <Layout>
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.ADMIN_APPLICATIONS} element={
                <Layout>
                  <ProtectedRoute 
                    roles={['ADMIN', 'SUPER_ADMIN', 'MANAGER']}
                    rememberRedirect={true}
                    onAccessDenied={(data) => console.warn('Access denied:', data)}
                  >
                    <AdminApplications />
                  </ProtectedRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.ADMIN_PROJECTS} element={
                <Layout>
                  <AdminRoute>
                    <AdminProjects />
                  </AdminRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.ADMIN_PROJECT_EDIT} element={
                <Layout>
                  <AdminRoute>
                    <AdminProjectEdit />
                  </AdminRoute>
                </Layout>
              } />
              
              {/* ===== ROLE-BASED DASHBOARD ROUTES ===== */}
              <Route path={ROUTES.MANAGER_DASHBOARD} element={
                <Layout>
                  <ManagerRoute>
                    <ManagerDashboard />
                  </ManagerRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.FINANCE_DASHBOARD} element={
                <Layout>
                  <FinanceRoute>
                    <FinanceDashboard />
                  </FinanceRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.CONTENT_MODERATION} element={
                <Layout>
                  <ContentModeratorRoute>
                    <ContentModerationDashboard />
                  </ContentModeratorRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.AGGREGATOR_DASHBOARD} element={
                <Layout>
                  <AggregatorRoute>
                    <AggregatorDashboard />
                  </AggregatorRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.CHANNEL_DASHBOARD} element={
                <Layout>
                  <ChannelOwnerRoute>
                    <ChannelOwnerDashboard />
                  </ChannelOwnerRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.CREATOR_DASHBOARD} element={
                <Layout>
                  <CreatorRoute>
                    <CreatorDashboard />
                  </CreatorRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.STUDIO_DASHBOARD} element={
                <Layout>
                  <StudioRoute>
                    <StudioDashboard />
                  </StudioRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.INVESTOR_DASHBOARD} element={
                <Layout>
                  <InvestorRoute>
                    <InvestorDashboard />
                  </InvestorRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.SUPPORT_DASHBOARD} element={
                <Layout>
                  <SupportRoute>
                    <SupportDashboard />
                  </SupportRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.ANALYTICS_DASHBOARD} element={
                <Layout>
                  <AnalyticsRoute>
                    <AnalyticsDashboard />
                  </AnalyticsRoute>
                </Layout>
              } />
              
              {/* ===== PROJECT ROUTES ===== */}
              <Route path={ROUTES.PROJECT_DETAIL} element={
                <Layout>
                  <DashboardRoute>
                    <ProjectDetail />
                  </DashboardRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.REVENUE_SHARE} element={
                <Layout>
                  <DashboardRoute>
                    <RevenueShareDashboard />
                  </DashboardRoute>
                </Layout>
              } />
              
              {/* ===== FEATURE ROUTES ===== */}
              <Route path={ROUTES.UPLOAD} element={
                <Layout>
                  <ProtectedRoute 
                    roles={['CREATOR', 'CONTENT_CREATOR', 'CHANNEL_OWNER', 'ADMIN', 'SUPER_ADMIN']}
                  >
                    <VideoUpload />
                  </ProtectedRoute>
                </Layout>
              } />
              
              {/* ===== CONTENT MANAGEMENT ROUTES ===== */}
              <Route path={ROUTES.CONTENT_PENDING} element={
                <Layout>
                  <ContentModeratorRoute>
                    <ContentModerationDashboard activeTab="pending" />
                  </ContentModeratorRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.CONTENT_APPROVED} element={
                <Layout>
                  <ContentModeratorRoute>
                    <ContentModerationDashboard activeTab="approved" />
                  </ContentModeratorRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.CONTENT_REJECTED} element={
                <Layout>
                  <ContentModeratorRoute>
                    <ContentModerationDashboard activeTab="rejected" />
                  </ContentModeratorRoute>
                </Layout>
              } />
              
             {/* ===== INVESTMENT ROUTES ===== */}
{/* Public route - no authentication required */}
<Route path={ROUTES.INVESTMENT_OPPORTUNITIES} element={
  <Layout>
    <InvestmentOpportunities />
  </Layout>
} />
              <Route path={ROUTES.INVESTMENTS} element={
                <Layout>
                  <InvestorRoute>
                    <InvestorDashboard activeTab="portfolio" />
                  </InvestorRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.INVESTMENT_MARKETPLACE} element={
                <Layout>
                  <InvestorRoute>
                    <InvestorDashboard activeTab="marketplace" />
                  </InvestorRoute>
                </Layout>
              } />
              
              {/* ===== AGGREGATOR ROUTES ===== */}
              <Route path={ROUTES.AGGREGATOR_REFERRALS} element={
                <Layout>
                  <AggregatorRoute>
                    <AggregatorDashboard activeTab="referrals" />
                  </AggregatorRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.AGGREGATOR_COMMISSIONS} element={
                <Layout>
                  <AggregatorRoute>
                    <AggregatorDashboard activeTab="commissions" />
                  </AggregatorRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.AGGREGATOR_APPLICATIONS} element={
                <Layout>
                  <AggregatorRoute>
                    <AggregatorDashboard activeTab="applications" />
                  </AggregatorRoute>
                </Layout>
              } />
              
              {/* ===== FINANCE ROUTES ===== */}
              <Route path={ROUTES.FINANCE_PAYMENTS} element={
                <Layout>
                  <FinanceRoute>
                    <FinanceDashboard activeTab="payments" />
                  </FinanceRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.FINANCE_REVENUE} element={
                <Layout>
                  <FinanceRoute>
                    <FinanceDashboard activeTab="revenue" />
                  </FinanceRoute>
                </Layout>
              } />
              
              {/* ===== ANALYTICS ROUTES ===== */}
              <Route path={ROUTES.ANALYTICS_OVERVIEW} element={
                <Layout>
                  <AnalyticsRoute>
                    <AnalyticsDashboard activeTab="overview" />
                  </AnalyticsRoute>
                </Layout>
              } />
              
              <Route path={ROUTES.ANALYTICS_CHANNELS} element={
                <Layout>
                  <AnalyticsRoute>
                    <AnalyticsDashboard activeTab="channels" />
                  </AnalyticsRoute>
                </Layout>
              } />
              
              {/* ===== REPORTS MODULE ===== */}
              <Route path="/reports" element={
                <Layout>
                  <ProtectedRoute>
                    <ReportsCatalog />
                  </ProtectedRoute>
                </Layout>
              } />
              <Route path="/reports/financial/revenue-trends" element={
                <Layout>
                  <ProtectedRoute>
                    <RevenueTrendsReport />
                  </ProtectedRoute>
                </Layout>
              } />
              <Route path="/reports/content/video-performance" element={
                <Layout>
                  <ProtectedRoute>
                    <VideoPerformanceReport />
                  </ProtectedRoute>
                </Layout>
              } />
              <Route path="/reports/people/creator-earnings" element={
                <Layout>
                  <ProtectedRoute>
                    <CreatorEarningsReport />
                  </ProtectedRoute>
                </Layout>
              } />
              <Route path="/reports/operations/audit-log" element={
                <Layout>
                  <ProtectedRoute>
                    <AuditLogReport />
                  </ProtectedRoute>
                </Layout>
              } />
              {/* The other 12 reports — now real (June 2026) */}
              <Route path="/reports/financial/payouts"             element={<Layout><ProtectedRoute><PayoutsReport /></ProtectedRoute></Layout>} />
              <Route path="/reports/financial/investments"         element={<Layout><ProtectedRoute><InvestmentFlowReport /></ProtectedRoute></Layout>} />
              <Route path="/reports/financial/pnl"                 element={<Layout><ProtectedRoute><PnLReport /></ProtectedRoute></Layout>} />
              <Route path="/reports/content/moderation"            element={<Layout><ProtectedRoute><ModerationPipelineReport /></ProtectedRoute></Layout>} />
              <Route path="/reports/content/cross-platform"        element={<Layout><ProtectedRoute><CrossPlatformReport /></ProtectedRoute></Layout>} />
              <Route path="/reports/channels/growth"               element={<Layout><ProtectedRoute><ChannelGrowthReport /></ProtectedRoute></Layout>} />
              <Route path="/reports/people/investor-portfolio"     element={<Layout><ProtectedRoute><InvestorPortfolioReport /></ProtectedRoute></Layout>} />
              <Route path="/reports/people/aggregator-commissions" element={<Layout><ProtectedRoute><AggregatorCommissionsReport /></ProtectedRoute></Layout>} />
              <Route path="/reports/people/user-activity"          element={<Layout><ProtectedRoute><UserActivityReport /></ProtectedRoute></Layout>} />
              <Route path="/reports/operations/application-funnel" element={<Layout><ProtectedRoute><ApplicationFunnelReport /></ProtectedRoute></Layout>} />
              <Route path="/reports/operations/system-health"      element={<Layout><ProtectedRoute><SystemHealthReport /></ProtectedRoute></Layout>} />
              <Route path="/reports/operations/tax"                element={<Layout><ProtectedRoute><TaxReport /></ProtectedRoute></Layout>} />

              {/* ===== HOMEPAGE ROUTE ===== */}
             <Route path={ROUTES.HOME} element={<Homepage />} />

              {/* ===== 404 ROUTE ===== */}
              <Route path="*" element={
                <Layout>
                  <NotFound />
                </Layout>
              } />
            </Routes>
          </Suspense>
          </Router>
        </UserProvider>
      </AntdProvider>
    </ErrorBoundary>
  );
}

export default App;